import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, FormControl, Select, MenuItem, Paper, IconButton, TextField } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { pageBoxSx, headerRowSx, leftTextSx, createButtonWrapperSx, controlsRowSx, selectSx, menuItemSx, allSelectCombinedSx, nodeDetailsSx, createButtonSx, bulkButtonSx, avatarSx, tableHeadSx, tableHeaderTextSx, bulkModalContainerSx, bulkModalHeaderSx, bulkModalTitleSx, bulkModalCloseSx, bulkModalBodySx, bulkTemplateBoxSx, bulkFileDropSx, bulkModalFooterSx, bulkModalCancelSx, bulkModalImportSx, bulkFileIconSx, bulkSelectTextSx, bulkBrowseTextSx, bulkTemplateFieldsSx, bulkDownloadTextSx, dispatcherSx, supervisorSx, addAccountSX } from './AuditLogs.styles';
import exportIcon from '../../assets/export-icon.svg';
import externalLinkIcon from '../../assets/externalLink-icon.svg';
import { getAuditLogs, AuditLogItem } from '../../services/auditService';
import { getAdminUsers } from '../../services/commandCentreService';
import { getMetadata } from '../../services/metadataService';


const AuditLogs: React.FC<{ onAdd?: () => void; onEdit?: (ccu: any) => void; onImported?: (message?: string) => void; notification?: { show: boolean; message?: string }; onOpenDetail?: (row: any) => void }> = ({ onAdd, onEdit, onImported, notification, onOpenDetail }) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [logs, setLogs] = useState<AuditLogItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [rowCount, setRowCount] = useState<number>(0);

    // server-side search: we no longer filter locally; logs is the last API page
    const filteredRows = logs;

    const gridContainerRef = useRef<HTMLDivElement | null>(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [filterFrom, setFilterFrom] = useState<string>('');
    const [filterTo, setFilterTo] = useState<string>('');
    const [filterAdminId, setFilterAdminId] = useState<string>('');
    const [filterEntity, setFilterEntity] = useState<string>('');
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const [adminList, setAdminList] = useState<Array<{ user_id: string; name: string; email: string }>>([]);
    const [entityList, setEntityList] = useState<Array<{ value: string; label: string }>>([]);

    const getEntityParam = (entity: string) => entity || undefined;

    // helper to load a specific page (page is zero-based here)
    const normalizeDateParam = (d?: string, isStart = true) => {
        if (!d) return undefined;
        if (d.includes('T')) return d;
        return isStart ? `${d}T00:00:00Z` : `${d}T23:59:59Z`;
    };

    // load admin list + entity list once on mount
    useEffect(() => {
        getAdminUsers(200).then(setAdminList).catch(() => {});
        getMetadata('audit_log_entity_list', true)
            .then((items) => setEntityList(items.map((i) => ({ value: i.value, label: i.label }))))
            .catch(() => {});
    }, []);

    const loadPage = async (pageZeroBased: number, pageSizeNum: number, from?: string, to?: string, q?: string, actorId?: string, entity?: string) => {
        setLoading(true);
        try {
            const apiPage = (pageZeroBased ?? 0) + 1;
            const apiPageSize = pageSizeNum ?? 10;
            const fromParam = normalizeDateParam(from ?? undefined, true);
            const toParam = normalizeDateParam(to ?? undefined, false);
            const resp = await getAuditLogs(apiPage, apiPageSize, fromParam, toParam, q ?? undefined, actorId, undefined, entity);
            setLogs((resp.logs || []).map((l: any) => ({ ...l })));
            const metaTotalRaw = resp?.meta?.total ?? resp?.meta?.total_count;
            const metaTotal = Number.isFinite(Number(metaTotalRaw)) ? Number(metaTotalRaw) : NaN;
            let total = 0;
            if (!Number.isNaN(metaTotal) && metaTotal > 0) {
                total = metaTotal;
            } else {
                const returned = Array.isArray(resp.logs) ? resp.logs.length : 0;
                if (apiPage <= 1) total = returned;
                else total = ((apiPage - 1) * apiPageSize) + returned;
            }
            setRowCount(total);
        } catch (err) {
            console.error('Failed to load audit logs', err);
            setLogs([]);
            setRowCount(0);
        } finally {
            setLoading(false);
        }
    };

    // initial load
    useEffect(() => { loadPage(0, paginationModel.pageSize ?? 10); }, []);

    // reload when date filters change
    useEffect(() => {
        setPaginationModel((m) => ({ ...m, page: 0 }));
        loadPage(0, paginationModel.pageSize ?? 10, filterFrom || undefined, filterTo || undefined, searchTerm?.trim() || undefined, filterAdminId || undefined, getEntityParam(filterEntity));
    }, [filterFrom, filterTo]);

    // reload when dropdown filters change
    useEffect(() => {
        setPaginationModel((m) => ({ ...m, page: 0 }));
        loadPage(0, paginationModel.pageSize ?? 10, filterFrom || undefined, filterTo || undefined, searchTerm?.trim() || undefined, filterAdminId || undefined, getEntityParam(filterEntity));
    }, [filterAdminId, filterEntity]);

    // debounce searchTerm
    useEffect(() => {
        const t = setTimeout(() => {
            setPaginationModel((m) => ({ ...m, page: 0 }));
            loadPage(0, paginationModel.pageSize ?? 10, filterFrom || undefined, filterTo || undefined, searchTerm?.trim() || undefined, filterAdminId || undefined, getEntityParam(filterEntity));
        }, 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const openDetail = (row: any) => {
        if (onOpenDetail) onOpenDetail(row);
    };

    const getActionColors = (action: string) => {
        const a = (action || '').toLowerCase();
        if (a === 'create') return { bg: '#ECFDF5', border: '#A7F3D0', text: '#60AF97' };
        if (a === 'update') return { bg: '#EFF6FF', border: '#BFDBFE', text: '#49B6D3' };
        return { bg: '#FEF2F2', border: '#FCA5A5', text: '#DE606C' };
    };

    const columns: GridColDef[] = [
        {
            field: 'timestamp', headerName: 'Timestamp', width: 200, sortable: false, renderCell: (params: GridRenderCellParams<any>) => {
                const row = params.row || {};
                const ts = row.timestamp ?? row.created_at ?? row.createdAt ?? row.time ?? row.ts ?? (row.data && row.data.timestamp) ?? (row.payload && row.payload.timestamp) ?? null;
                return (<Typography sx={{ fontFamily: 'Cousine, monospace', fontSize: 12, lineHeight: '18px', color: '#374151' }}>{ts ? new Date(ts).toLocaleString() : ''}</Typography>);
            }
        },
        {
            field: 'actor', headerName: 'Actor', width: 250, sortable: false, renderCell: (params: GridRenderCellParams<any>) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: 13, color: '#111827', lineHeight: '20px' }}>{params.row.actor ?? 'System'}</Typography>
                    {params.row.actor_email && (
                        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 11, color: '#9CA3AF', lineHeight: '16px' }}>{params.row.actor_email}</Typography>
                    )}
                </Box>
            )
        },
        {
            field: 'action', headerName: 'Action', width: 150, sortable: false, renderCell: (params: GridRenderCellParams<any>) => {
                const colors = getActionColors(params.value || '');
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: '9999px', px: 1.5, height: '24px', display: 'inline-flex', alignItems: 'center' }}>
                            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: 11, color: colors.text, lineHeight: 1 }}>{(params.value || '').toString().toUpperCase()}</Typography>
                        </Box>
                    </Box>
                );
            }
        },
        {
            field: 'target', headerName: 'Target', width: 360, sortable: false, renderCell: (params: GridRenderCellParams<any>) => {
                const entity = params.row.target_entity || '';
                const name = params.row.target_name || '';
                return (
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 13, color: '#374151', fontWeight: 400 }}>
                        {entity}{name ? `: ${name}` : ''}
                    </Typography>
                );
            }
        },
        {
            field: 'view', headerName: '', width: 48, sortable: false, renderCell: (params: GridRenderCellParams<any>) => (
                <IconButton size="small" sx={{ padding: 0 }} onClick={() => openDetail(params.row)}>
                    <Box component="img" src={externalLinkIcon} alt="Open" sx={{ width: 13, height: 13, display: 'block' }} />
                </IconButton>
            )
        },
    ];

    // custom no-rows message via componentsProps
    const noRowsMessage = searchTerm.trim() ? 'No match found' : 'No rows';

    const handleExport = async () => {
        setExportLoading(true);
        try {
            const today = new Date().toISOString().slice(0, 10);
            const fromDate = filterFrom || (filterTo ? today : undefined);
            const toDate = filterTo || (filterFrom ? today : undefined);
            const normalize = (d?: string, start = true) => {
                if (!d) return undefined;
                if (d.includes('T')) return d;
                return start ? `${d}T00:00:00Z` : `${d}T23:59:59Z`;
            };
            const fromParam = normalize(fromDate, true);
            const toParam = normalize(toDate, false);
            const actorParam = filterAdminId || undefined;
            const entityParam = filterEntity || undefined;
            const { exportAuditLogs } = await import('../../services/auditService');
            const blob = await exportAuditLogs(fromParam, toParam, actorParam, entityParam);
            if (!blob || blob.size === 0) throw new Error('No data received for export');
            const textPeek = await blob.slice(0, 2000).text();
            try {
                const maybe = JSON.parse(textPeek);
                if (maybe && maybe.message) throw new Error(maybe.message);
            } catch (e) { /* not JSON, proceed */ }
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit-logs-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: 'Export downloaded' } }));
        } catch (err: any) {
            window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? 'Export failed' } }));
        } finally {
            setExportLoading(false);
        }
    };

    const hasActiveFilters = !!(filterAdminId || filterEntity);

    return (
        <Box sx={pageBoxSx}>
            {/* Right filter panel backdrop */}
            {filterPanelOpen && (
                <Box onClick={() => setFilterPanelOpen(false)}
                    sx={{ position: 'fixed', inset: 0, zIndex: 1199, background: 'rgba(0,0,0,0.18)' }} />
            )}

            {/* Right filter panel */}
            <Box sx={{
                position: 'fixed', top: 56, right: 0, width: 300, height: 'calc(100vh - 56px)',
                background: '#FFFFFF', borderLeft: '1px solid #E5E7EB', zIndex: 1200,
                transform: filterPanelOpen ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.25s ease',
                display: 'flex', flexDirection: 'column',
                boxShadow: filterPanelOpen ? '-4px 0 24px rgba(0,0,0,0.08)' : 'none',
            }}>
                {/* Panel header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #E5E7EB' }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: 15, color: '#111827' }}>Filters</Typography>
                    <IconButton size="small" onClick={() => setFilterPanelOpen(false)} sx={{ p: 0.5 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round"/></svg>
                    </IconButton>
                </Box>

                {/* Panel body */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Admin filter */}
                    <Box>
                        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: 12, color: '#6B7280', mb: 1 }}>Admin</Typography>
                        <FormControl size="small" fullWidth>
                            <Select value={filterAdminId} onChange={(e) => setFilterAdminId(e.target.value)}
                                displayEmpty
                                sx={{ borderRadius: '10px', fontSize: 13, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' } }}>
                                <MenuItem value="">All admins</MenuItem>
                                {adminList.map((a) => (
                                    <MenuItem key={a.user_id} value={a.user_id}>{a.name || a.email}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Entity filter */}
                    <Box>
                        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: 12, color: '#6B7280', mb: 1 }}>Entity type</Typography>
                        <FormControl size="small" fullWidth>
                            <Select value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)}
                                displayEmpty
                                sx={{ borderRadius: '10px', fontSize: 13, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' } }}>
                                <MenuItem value="">All entity types</MenuItem>
                                {entityList.length > 0
                                    ? entityList.map((e) => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)
                                    : ['Node', 'Guard', 'CommandCentre', 'CommandUser', 'LEStation', 'LEUser', 'AdminUser'].map((v) => (
                                        <MenuItem key={v} value={v}>{v}</MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Panel footer */}
                {hasActiveFilters && (
                    <Box sx={{ px: 3, py: 2, borderTop: '1px solid #E5E7EB' }}>
                        <Button fullWidth variant="outlined" onClick={() => { setFilterAdminId(''); setFilterEntity(''); }}
                            sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: 13, color: '#6B7280', borderColor: '#E5E7EB' }}>
                            Clear filters
                        </Button>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                <Box sx={{ width: 1008, minWidth: 0 }}>
                    <Box mb={2}>
                        <Box sx={{ ...headerRowSx, width: '100%', left: 0, position: 'relative' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '231.4px' }}>
                                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 700, fontSize: '20px', lineHeight: '30px', color: '#111827' }}>Audit Log</Typography>
                                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '20px', color: '#6B7280' }}>Immutable record of all admin actions.</Typography>
                            </Box>

                            <Box sx={{ position: 'absolute', right: 0, top: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    disabled={exportLoading}
                                    onClick={handleExport}
                                    sx={{ width: '126.5625px', height: '37.5px', borderRadius: '10px', border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#374151', textTransform: 'none', display: 'flex', alignItems: 'center', gap: 1, px: 1.5, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '20px' }}>
                                    <Box component="img" src={exportIcon} alt="Export" sx={{ width: 14, height: 14, display: 'block' }} />
                                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '20px', color: '#374151' }}>Export CSV</Typography>
                                </Button>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', mt: 5 }}>
                            {/* From date — KAN-148: cap at today, no future dates allowed */}
                            <Box sx={{ height: 36, display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#6B7280', whiteSpace: 'nowrap' }}>From</Typography>
                                <TextField size="small" type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)}
                                    inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                                    sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: '10px', height: 36, '& fieldset': { borderColor: '#E5E7EB' }, '& .MuiOutlinedInput-input': { padding: '8px 12px' } } }} />
                            </Box>
                            {/* To date — KAN-148: cap at today and require To >= From */}
                            <Box sx={{ height: 36, display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#6B7280', whiteSpace: 'nowrap' }}>To</Typography>
                                <TextField size="small" type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)}
                                    inputProps={{ min: filterFrom || undefined, max: new Date().toISOString().slice(0, 10) }}
                                    sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: '10px', height: 36, '& fieldset': { borderColor: '#E5E7EB' }, '& .MuiOutlinedInput-input': { padding: '8px 12px' } } }} />
                            </Box>
                            {/* Filters toggle */}
                            <Button variant="outlined" onClick={() => setFilterPanelOpen((o) => !o)} sx={{ height: 36, flexShrink: 0, borderRadius: '10px', border: (hasActiveFilters || filterPanelOpen) ? '1px solid #39A9B1' : '1px solid #E5E7EB', background: (hasActiveFilters || filterPanelOpen) ? '#F0FAFA' : '#FFFFFF', color: (hasActiveFilters || filterPanelOpen) ? '#39A9B1' : '#374151', textTransform: 'none', px: 1.5, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px' }}>
                                Filters{hasActiveFilters ? ' •' : ''}
                            </Button>
                            {/* Search — stretches to match datagrid right edge */}
                            <Box sx={{ flex: 1, height: 36, border: '1px solid #E5E7EB', borderRadius: '10px', display: 'flex', alignItems: 'center', background: '#FFFFFF' }}>
                                <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by admin name or email." style={{ border: 'none', outline: 'none', width: '100%', fontSize: 13, color: 'rgba(55,65,81,0.5)', padding: '8px 12px', background: 'transparent' }} />
                            </Box>
                        </Box>
                    </Box>

                    <Paper sx={{ ...nodeDetailsSx, width: '100%', border: '1px solid #E5E7EB', borderRadius: '14px', overflow: 'hidden' }}>
                        <div ref={gridContainerRef} style={{ height: '100%', width: '100%' }}>
                            <DataGrid
                                paginationMode="server"
                                rows={filteredRows}
                                columns={columns}
                                columnHeaderHeight={41}
                                rowHeight={73}
                                pagination
                                paginationModel={paginationModel}
                                onPaginationModelChange={(model: { page: number; pageSize: number }) => {
                                    setPaginationModel(model);
                                    loadPage(model.page ?? 0, model.pageSize ?? 10, filterFrom || undefined, filterTo || undefined, searchTerm?.trim() || undefined, filterAdminId || undefined, getEntityParam(filterEntity));
                                }}
                                pageSizeOptions={[5, 10, 25, 50]}
                                density="comfortable"
                                disableRowSelectionOnClick
                                rowCount={rowCount}
                                loading={loading}
                                getRowId={(r) => r.id ?? `${r.target_entity}-${r.target_id}-${r.timestamp}`}
                                sx={{
                                    '& .MuiDataGrid-root': { position: 'relative' },
                                    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F9FAFB !important', borderBottom: '1px solid #E5E7EB', minHeight: '41px !important', maxHeight: '41px !important', position: 'sticky', top: 0, zIndex: 2 },
                                    '& .MuiDataGrid-virtualScroller': { maxHeight: 'calc(100vh - 260px)', overflow: 'auto', paddingBottom: '56px' },
                                    '& .MuiDataGrid-footerContainer': { position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 4, background: '#FFFFFF', borderTop: '1px solid rgba(0,0,0,0.06)' },
                                    '& .MuiDataGrid-columnHeader': { backgroundColor: '#F9FAFB !important', minHeight: '41px !important', maxHeight: '41px !important', display: 'flex', alignItems: 'center', borderRight: 'none' },
                                    '& .MuiDataGrid-columnHeaderTitle': { fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '11px', lineHeight: '16.5px', letterSpacing: '0.06px' },
                                    '& .MuiDataGrid-columnSeparator': { display: 'none' },
                                    '& .MuiDataGrid-cell': { borderRight: 'none', borderBottom: '1px solid #F3F4F6' },
                                    '& .MuiDataGrid-row': { borderBottom: '1px solid #F3F4F6' },
                                    '& .MuiDataGrid-cellContent, & .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                }}
                            />
                        </div>
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

export default AuditLogs;
